import React, { useState, useRef, useEffect, useCallback } from "react";
import Mic from "@mui/icons-material/Mic";
import MicNone from "@mui/icons-material/MicNone";
import Send from "@mui/icons-material/Send";
import Add from "@mui/icons-material/Add";
import ViewSidebar from "@mui/icons-material/ViewSidebar";
import Search from "@mui/icons-material/Search";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import TrendingDownOutlined from "@mui/icons-material/TrendingDownOutlined";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import BarChartOutlined from "@mui/icons-material/BarChartOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import SupervisorAccountOutlined from "@mui/icons-material/SupervisorAccountOutlined";
import SchoolOutlined from "@mui/icons-material/SchoolOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { useLocation, useNavigate } from "react-router-dom";
import Auth from "../../utils/auth";
import "./BiAgent.css";

// ── Config ──────────────────────────────────────────────────
const N8N_WEBHOOK_URL = "https://evrsyn.app.n8n.cloud/webhook/bi-agent-chat";
const REQUEST_TIMEOUT = 90_000;
const VOICE_OUTPUT_STORAGE_KEY = "biAgentVoiceOutputEnabled";
const LS_ACTIVE_CONV = "biAgent_activeConvId";
const LS_CONV_LIST = "biAgent_conversations";
const LS_CONV_PREFIX = "biAgent_conv_";

// ── Helpers ─────────────────────────────────────────────────
const generateConversationId = () =>
  `conv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// ── localStorage persistence helpers ────────────────────────
const lsGet = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};
const lsSet = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};
const lsRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {}
};

const loadConversationList = () => lsGet(LS_CONV_LIST, []);

const saveConversationList = (list) => lsSet(LS_CONV_LIST, list);

const saveConversationMessages = (convId, msgs) => {
  if (!convId || !msgs.length) return;
  lsSet(LS_CONV_PREFIX + convId, msgs);
};

const loadConversationMessages = (convId) => lsGet(LS_CONV_PREFIX + convId, []);

const deleteConversation = (convId) => {
  lsRemove(LS_CONV_PREFIX + convId);
  const list = loadConversationList().filter((c) => c.id !== convId);
  saveConversationList(list);
};

const upsertConversationInList = (convId, msgs) => {
  const list = loadConversationList();
  const firstUserMsg = msgs.find((m) => m.role === "user");
  const title = firstUserMsg
    ? firstUserMsg.text.slice(0, 60)
    : "New conversation";
  const existing = list.findIndex((c) => c.id === convId);
  const entry = {
    id: convId,
    title,
    updated_at: new Date().toISOString(),
    messageCount: msgs.length,
  };
  if (existing >= 0) {
    list[existing] = entry;
  } else {
    list.unshift(entry);
  }
  saveConversationList(list.slice(0, 50));
};

const stripMarkdown = (text) =>
  text
    .replace(/```[\s\S]*?```/g, "")
    .replace(/\|[^\n]+\|/g, "")
    .replace(/[-]{3,}/g, "")
    .replace(/#{1,6}\s*/g, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[>\-*+] /g, "")
    .replace(/\n{2,}/g, ". ")
    .replace(/\n/g, " ")
    .trim();

const LOADING_STEPS = [
  {
    title: "Loading portfolio data",
    subtitle: "Gathering client health context",
  },
  {
    title: "Analyzing patterns",
    subtitle: "Comparing CRS, engagement, and risk trends",
  },
  {
    title: "Running deep analysis",
    subtitle: "Preparing a narrative and actionable output",
  },
];

const SUGGESTED_QUESTIONS = [
  "Which clients are the least engaged right now?",
  "What did Joe Smith need help with on his campaign from our call last week?",
  "Which of my CSMs has the clients with the lowest CRS?",
  "Are there any clients who have expressed interest in the event next week?",
];

const STARTER_CARDS = [
  {
    key: "risk",
    title: "Risk & Retention",
    icon: <TrendingDownOutlined sx={{ fontSize: 18 }} />,
    lines: [
      "Which of my clients should I reach out to this week?",
      "Who is at the highest risk of leaving right now?",
    ],
    className: "bi-agent-starter-risk",
  },
  {
    key: "engagement",
    title: "Engagement",
    icon: <PhoneOutlined sx={{ fontSize: 18 }} />,
    lines: [
      "Which clients haven't heard from me in the longest time?",
      "Who on my team has the least engagement with their clients?",
    ],
    className: "bi-agent-starter-engagement",
  },
  {
    key: "portfolio",
    title: "Portfolio",
    icon: <GroupsOutlined sx={{ fontSize: 18 }} />,
    lines: [
      "Give me a full health summary of my client portfolio.",
      "Which clients are performing best right now?",
    ],
    className: "bi-agent-starter-portfolio",
  },
  {
    key: "analysis",
    title: "Analysis",
    icon: <BarChartOutlined sx={{ fontSize: 18 }} />,
    lines: [
      "What patterns are showing up across my portfolio this month?",
      "Which of my clients are most likely to expand their contract?",
    ],
    className: "bi-agent-starter-analysis",
  },
  {
    key: "billing",
    title: "Billing",
    icon: <DescriptionOutlined sx={{ fontSize: 18 }} />,
    lines: [
      "Which clients have a payment issue I should know about?",
      "Are there any billing trends that could signal a problem?",
    ],
    className: "bi-agent-starter-billing",
  },
  {
    key: "lookup",
    title: "Lookup",
    icon: <SearchOutlined sx={{ fontSize: 18 }} />,
    lines: [
      "What's been happening with my lowest-scoring client?",
      "Show me the full history on my longest-running client.",
    ],
    className: "bi-agent-starter-lookup",
  },
  {
    key: "team",
    title: "Team",
    icon: <SupervisorAccountOutlined sx={{ fontSize: 18 }} />,
    lines: [
      "Which team member's clients need the most attention?",
      "How does my team's client health compare across accounts?",
    ],
    className: "bi-agent-starter-team",
  },
  {
    key: "onboarding",
    title: "Onboarding",
    icon: <SchoolOutlined sx={{ fontSize: 18 }} />,
    lines: [
      "Which new clients haven't completed their onboarding yet?",
      "Are any of my recently signed clients showing early warning signs?",
    ],
    className: "bi-agent-starter-onboarding",
  },
];

const getSpeechRecognition = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

// ── Main Component ──────────────────────────────────────────
const BiAgent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(8);
  const [hasFirstChunk, setHasFirstChunk] = useState(false);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  // Conversation — restore from localStorage
  const [conversationId, setConversationId] = useState(() => {
    const saved = lsGet(LS_ACTIVE_CONV, null);
    return saved || generateConversationId();
  });
  const [messages, setMessages] = useState(() => {
    const saved = lsGet(LS_ACTIVE_CONV, null);
    return saved ? loadConversationMessages(saved) : [];
  });
  const [conversations, setConversations] = useState(loadConversationList);

  // Voice
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceOutputEnabled] = useState(() => {
    try {
      return localStorage.getItem(VOICE_OUTPUT_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [speechSupport, setSpeechSupport] = useState({
    recognition: false,
    synthesis: false,
  });

  // Refs
  const recognitionRef = useRef(null);
  const synthesisRef = useRef(null);
  const messagesEndRef = useRef(null);
  const stoppedByUserRef = useRef(false);
  const inputRef = useRef(null);
  const isListeningRef = useRef(false);
  const ignoreSpeechInputRef = useRef(false);
  const abortRef = useRef(null);
  const timerIdsRef = useRef([]);

  const registerTimer = (id) => {
    timerIdsRef.current.push(id);
    return id;
  };
  const clearAllTimers = () => {
    timerIdsRef.current.forEach((id) => {
      clearTimeout(id);
      clearInterval(id);
    });
    timerIdsRef.current = [];
  };

  // ── Effects ─────────────────────────────────────────────
  useEffect(() => {
    const Recognition = getSpeechRecognition();
    setSpeechSupport({
      recognition: !!Recognition,
      synthesis: typeof window !== "undefined" && "speechSynthesis" in window,
    });
    return () => {
      clearAllTimers();
      abortRef.current?.abort();
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (_) {}
      }
      if (typeof window !== "undefined" && window.speechSynthesis)
        window.speechSynthesis.cancel();
    };
  }, []);

  // Persist messages + conversationId to localStorage
  useEffect(() => {
    if (!conversationId) return;
    lsSet(LS_ACTIVE_CONV, conversationId);
    // Only persist non-empty, non-streaming conversations
    const settled = messages.filter((m) => !m.isStreaming && m.text);
    if (settled.length > 0) {
      saveConversationMessages(conversationId, settled);
      upsertConversationInList(conversationId, settled);
      setConversations(loadConversationList());
    }
  }, [messages, conversationId]);

  useEffect(() => {
    try {
      localStorage.setItem(
        VOICE_OUTPUT_STORAGE_KEY,
        String(voiceOutputEnabled),
      );
    } catch (_) {}
  }, [voiceOutputEnabled]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isProcessing, hasFirstChunk]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [inputValue]);

  // ── TTS ─────────────────────────────────────────────────
  const speakResponse = useCallback(
    (text) => {
      if (!speechSupport.synthesis || !text) return;
      window.speechSynthesis.cancel();
      const clean = stripMarkdown(text);
      if (!clean) return;
      const utterance = new SpeechSynthesisUtterance(clean);
      utterance.rate = 0.95;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      synthesisRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    },
    [speechSupport.synthesis],
  );

  // ── Loading simulation (uses other dev's UI) ────────────
  const startLoadingSimulation = useCallback(() => {
    setLoadingStepIndex(0);
    setLoadingProgress(8);
    registerTimer(
      setInterval(() => {
        setLoadingStepIndex((prev) =>
          Math.min(prev + 1, LOADING_STEPS.length - 1),
        );
        setLoadingProgress((prev) => {
          const next = prev + 14;
          return next > 92 ? 92 : next;
        });
      }, 950),
    );
  }, []);

  // ── Send message to n8n ─────────────────────────────────
  const handleSubmit = useCallback(
    async (textOverride) => {
      const text = (textOverride ?? inputValue)?.trim();
      if (!text || isProcessing) return;

      ignoreSpeechInputRef.current = true;

      if (isListeningRef.current) {
        stoppedByUserRef.current = true;
        try {
          if (recognitionRef.current) recognitionRef.current.stop();
        } catch (_) {}
        recognitionRef.current = null;
        isListeningRef.current = false;
        setIsListening(false);
      }

      clearAllTimers();
      setError(null);
      setInputValue("");

      // Add user message optimistically
      const userMsgId = `user-${Date.now()}`;
      const assistantMsgId = `assistant-${Date.now()}`;

      setMessages((prev) => [
        ...prev,
        { id: userMsgId, role: "user", text },
        {
          id: assistantMsgId,
          role: "assistant",
          text: "",
          payload: null,
          isStreaming: true,
        },
      ]);

      // Build history from PRIOR messages (before this user msg)
      const history = messages
        .slice(-6)
        .map((m) => ({ role: m.role, content: m.text }));

      // Start loading UI
      setIsProcessing(true);
      setHasFirstChunk(false);
      startLoadingSimulation();

      const controller = new AbortController();
      abortRef.current = controller;
      const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      try {
        const token = Auth.getToken();
        const user = Auth.getUser?.() || {};
        const wid = Auth.getWorkspaceId() || "";

        const res = await fetch(N8N_WEBHOOK_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            question: text,
            company_id: String(wid),
            conversation_id: conversationId,
            user_id: String(user.id || ""),
            history: history.length > 0 ? history : undefined,
          }),
          signal: controller.signal,
        });

        clearTimeout(timeout);

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.message || `Server error (${res.status})`);
        }

        const data = await res.json();
        const responseText = data.response || "No response received.";

        // Complete the loading animation
        clearAllTimers();
        setLoadingStepIndex(LOADING_STEPS.length);
        setLoadingProgress(100);

        // Brief pause then show response (matching other dev's UX pattern)
        await new Promise((r) => setTimeout(r, 350));
        setHasFirstChunk(true);

        // Word-by-word streaming of the real response (reusing other dev's pattern)
        const words = responseText.split(" ");
        let cursor = 0;
        const streamId = registerTimer(
          setInterval(() => {
            if (cursor >= words.length) {
              clearInterval(streamId);
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        text: responseText,
                        payload: null,
                        isStreaming: false,
                      }
                    : msg,
                ),
              );
              setIsProcessing(false);
              if (voiceOutputEnabled) speakResponse(responseText);
              return;
            }
            const nextText = words.slice(0, cursor + 1).join(" ");
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMsgId
                  ? { ...msg, text: nextText, isStreaming: true }
                  : msg,
              ),
            );
            cursor += 1;
          }, 30), // Fast streaming for real responses (30ms vs 125ms for mock)
        );
      } catch (err) {
        clearTimeout(timeout);
        clearAllTimers();
        // Remove the empty assistant placeholder
        setMessages((prev) => prev.filter((m) => m.id !== assistantMsgId));
        setIsProcessing(false);
        setHasFirstChunk(false);
        if (err.name === "AbortError") {
          setError(
            "Request timed out. The AI is taking longer than usual - please try a simpler question.",
          );
        } else {
          setError(err.message || "Something went wrong. Please try again.");
        }
      } finally {
        abortRef.current = null;
      }
    },
    [
      inputValue,
      isProcessing,
      messages,
      conversationId,
      voiceOutputEnabled,
      speakResponse,
      startLoadingSimulation,
    ],
  );

  // ── Voice recognition ───────────────────────────────────
  const startRecognition = useCallback(() => {
    const Recognition = getSpeechRecognition();
    if (!Recognition || !isListeningRef.current) return null;

    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      if (ignoreSpeechInputRef.current) return;
      let fullTranscript = "";
      for (let i = 0; i < event.results.length; i += 1) {
        fullTranscript += event.results[i][0]?.transcript ?? "";
      }
      const trimmed = fullTranscript.trim();
      if (trimmed) setInputValue(trimmed);
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      if (stoppedByUserRef.current) {
        stoppedByUserRef.current = false;
        isListeningRef.current = false;
        setIsListening(false);
        return;
      }
      if (isListeningRef.current) startRecognition();
      else setIsListening(false);
    };

    recognition.onerror = (event) => {
      if (event.error === "aborted" || stoppedByUserRef.current) return;
      if (event.error !== "no-speech") {
        isListeningRef.current = false;
        setIsListening(false);
      }
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      return recognition;
    } catch (_) {
      return null;
    }
  }, []);

  const toggleListening = useCallback(() => {
    const Recognition = getSpeechRecognition();
    if (!Recognition) return;

    if (isListening) {
      stoppedByUserRef.current = true;
      try {
        recognitionRef.current?.stop();
      } catch (_) {}
      recognitionRef.current = null;
      isListeningRef.current = false;
      setIsListening(false);
      return;
    }

    setInputValue("");
    ignoreSpeechInputRef.current = false;
    isListeningRef.current = true;
    setIsListening(true);
    startRecognition();
  }, [isListening, startRecognition]);

  // ── New chat ────────────────────────────────────────────
  const handleNewChat = useCallback(() => {
    abortRef.current?.abort();
    clearAllTimers();
    if (speechSupport.synthesis) window.speechSynthesis.cancel();
    setIsSpeaking(false);
    setIsProcessing(false);
    setHasFirstChunk(false);
    setLoadingProgress(8);
    setLoadingStepIndex(0);
    setMessages([]);
    setInputValue("");
    setError(null);
    const newId = generateConversationId();
    setConversationId(newId);
    lsSet(LS_ACTIVE_CONV, newId);
  }, [speechSupport.synthesis]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("nav") !== "1") return;
    handleNewChat();
    navigate("/bi-agent", { replace: true });
  }, [location.search, navigate, handleNewChat]);

  // ── Select conversation from sidebar ────────────────────
  const handleSelectConversation = useCallback(
    (convId) => {
      if (convId === conversationId) return;
      abortRef.current?.abort();
      clearAllTimers();
      setIsProcessing(false);
      setHasFirstChunk(false);
      setError(null);
      const msgs = loadConversationMessages(convId);
      setConversationId(convId);
      setMessages(msgs);
      lsSet(LS_ACTIVE_CONV, convId);
    },
    [conversationId],
  );

  // ── Delete conversation from sidebar ────────────────────
  const handleDeleteConversation = useCallback(
    (convId, e) => {
      e.stopPropagation();
      deleteConversation(convId);
      setConversations(loadConversationList());
      // If deleting the active conversation, start fresh
      if (convId === conversationId) {
        const newId = generateConversationId();
        setConversationId(newId);
        setMessages([]);
        lsSet(LS_ACTIVE_CONV, newId);
      }
    },
    [conversationId],
  );

  // ── Render ──────────────────────────────────────────────
  const hasMessages = messages.length > 0;
  const filteredConversations = conversations.filter((conv) =>
    (conv.title || "New conversation")
      .toLowerCase()
      .includes(searchValue.trim().toLowerCase()),
  );
  const now = new Date();
  const groupedConversations = filteredConversations.reduce(
    (acc, conv) => {
      const updated = conv.updated_at ? new Date(conv.updated_at) : null;
      const isValid = updated && !Number.isNaN(updated.getTime());
      const sameDay =
        isValid &&
        updated.getDate() === now.getDate() &&
        updated.getMonth() === now.getMonth() &&
        updated.getFullYear() === now.getFullYear();
      const inLastWeek =
        isValid && !sameDay && now.getTime() - updated.getTime() < 7 * 86400000;

      if (sameDay) acc.today.push(conv);
      else if (inLastWeek) acc.thisWeek.push(conv);
      else acc.older.push(conv);
      return acc;
    },
    { today: [], thisWeek: [], older: [] },
  );

  return (
    <div className="bi-agent-page">
      <aside
        className={`bi-agent-sidebar ${sidebarOpen ? "bi-agent-sidebar-open" : "bi-agent-sidebar-collapsed"}`}
      >
        {sidebarOpen ? (
          <>
            <div className="bi-agent-your-chats-card">
              <button
                type="button"
                className="bi-agent-btn bi-agent-btn-primary bi-agent-new-chat-btn"
                onClick={handleNewChat}
              >
                <Add sx={{ fontSize: 18 }} /> New Conversation
              </button>
              <div className="bi-agent-search-wrap">
                <Search sx={{ fontSize: 15 }} />
                <input
                  className="bi-agent-search-input"
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search Conversations"
                />
              </div>
              <div className="bi-agent-sidebar-content">
                {filteredConversations.length === 0 ? (
                  <p className="bi-agent-sidebar-empty">
                    No conversations yet. Ask the BI Agent something to get
                    started.
                  </p>
                ) : (
                  <>
                    {groupedConversations.today.length > 0 && (
                      <div className="bi-agent-conv-section">
                        <span className="bi-agent-conv-section-label">TODAY</span>
                        {groupedConversations.today.map((conv) => (
                          <div
                            key={conv.id}
                            className={`bi-agent-conv-item ${conv.id === conversationId ? "bi-agent-conv-active" : ""}`}
                            onClick={() => handleSelectConversation(conv.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                              e.key === "Enter" &&
                              handleSelectConversation(conv.id)
                            }
                          >
                            <ChatBubbleOutline sx={{ fontSize: 12 }} />
                            <span className="bi-agent-conv-title">
                              {conv.title || "New conversation"}
                            </span>
                            <span className="bi-agent-conv-count">
                              {conv.messageCount || 0}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {groupedConversations.thisWeek.length > 0 && (
                      <div className="bi-agent-conv-section">
                        <span className="bi-agent-conv-section-label">THIS WEEK</span>
                        {groupedConversations.thisWeek.map((conv) => (
                          <div
                            key={conv.id}
                            className={`bi-agent-conv-item ${conv.id === conversationId ? "bi-agent-conv-active" : ""}`}
                            onClick={() => handleSelectConversation(conv.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                              e.key === "Enter" &&
                              handleSelectConversation(conv.id)
                            }
                          >
                            <ChatBubbleOutline sx={{ fontSize: 12 }} />
                            <span className="bi-agent-conv-title">
                              {conv.title || "New conversation"}
                            </span>
                            <span className="bi-agent-conv-count">
                              {conv.messageCount || 0}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {groupedConversations.older.length > 0 && (
                      <div className="bi-agent-conv-section">
                        <span className="bi-agent-conv-section-label">OLDER</span>
                        {groupedConversations.older.map((conv) => (
                          <div
                            key={conv.id}
                            className={`bi-agent-conv-item ${conv.id === conversationId ? "bi-agent-conv-active" : ""}`}
                            onClick={() => handleSelectConversation(conv.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                              e.key === "Enter" &&
                              handleSelectConversation(conv.id)
                            }
                          >
                            <ChatBubbleOutline sx={{ fontSize: 12 }} />
                            <span className="bi-agent-conv-title">
                              {conv.title || "New conversation"}
                            </span>
                            <span className="bi-agent-conv-count">
                              {conv.messageCount || 0}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <button
              type="button"
              className="bi-agent-sidebar-collapse-btn"
              onClick={() => setSidebarOpen(false)}
            >
              <KeyboardArrowLeft sx={{ fontSize: 16 }} />
              Collapse
            </button>
          </>
        ) : (
          <div className="bi-agent-collapsed-inner">
            <div className="bi-agent-collapsed-pill">
              <button
                type="button"
                className="bi-agent-collapsed-action"
                onClick={() => setSidebarOpen(true)}
                title="Open sidebar"
                aria-label="Open sidebar"
              >
                <ViewSidebar sx={{ fontSize: 20 }} />
              </button>
            </div>
          </div>
        )}
      </aside>

      <div className="bi-agent-left-column">
        <main className="bi-agent-main">
          {hasMessages ? (
            <div className="bi-agent-messages">
              {messages.map((msg) => {
                if (
                  msg.role === "assistant" &&
                  !hasFirstChunk &&
                  !msg.payload &&
                  !msg.text
                ) {
                  return null;
                }
                return (
                  <div
                    key={msg.id}
                    className={`bi-agent-message bi-agent-message-${msg.role}`}
                  >
                    <span className="bi-agent-message-role">
                      {msg.role === "user" ? "You" : "BI Agent"}
                    </span>

                    {msg.role === "assistant" && msg.payload ? (
                      /* Structured payload rendering (preserved from other dev) */
                      <div className="bi-agent-assistant-content">
                        <p className="bi-agent-message-text bi-agent-summary-text">
                          {msg.payload.summary}
                        </p>

                        {msg.payload.table && (
                          <div className="bi-agent-table-wrap">
                            <table className="bi-agent-data-table">
                              <thead>
                                <tr>
                                  {msg.payload.table.headers.map((header) => (
                                    <th key={header}>{header}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {msg.payload.table.rows.map((row, rowIndex) => (
                                  <tr key={`${msg.id}-row-${rowIndex}`}>
                                    {row.map((cell, cellIndex) => (
                                      <td
                                        key={`${msg.id}-${rowIndex}-${cellIndex}`}
                                      >
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {Array.isArray(msg.payload.sections) &&
                          msg.payload.sections.map((section) => (
                            <div
                              className="bi-agent-section"
                              key={`${msg.id}-${section.title}`}
                            >
                              <h4>{section.title}</h4>
                              <p className="bi-agent-message-text">
                                {section.body}
                              </p>
                            </div>
                          ))}

                        {msg.payload.watchOut && (
                          <p className="bi-agent-watchout">
                            <span>Watch Out: </span>
                            {msg.payload.watchOut}
                          </p>
                        )}
                      </div>
                    ) : msg.role === "assistant" ? (
                      /* n8n markdown response rendering */
                      <div className="bi-agent-message-text bi-agent-markdown">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeSanitize]}
                          components={{
                            h1: ({ children }) => (
                              <h2 className="bi-agent-md-heading bi-agent-md-h2">
                                {children}
                              </h2>
                            ),
                            h2: ({ children }) => (
                              <h2 className="bi-agent-md-heading bi-agent-md-h2">
                                {children}
                              </h2>
                            ),
                            h3: ({ children }) => (
                              <h3 className="bi-agent-md-heading bi-agent-md-h3">
                                {children}
                              </h3>
                            ),
                            h4: ({ children }) => (
                              <h4 className="bi-agent-md-heading bi-agent-md-h4">
                                {children}
                              </h4>
                            ),
                          }}
                        >
                          {msg.text}
                        </ReactMarkdown>
                        {msg.isStreaming && msg.text && (
                          <span className="bi-agent-streaming-tag">
                            Streaming...
                          </span>
                        )}
                      </div>
                    ) : (
                      /* User message */
                      <p className="bi-agent-message-text">{msg.text}</p>
                    )}
                  </div>
                );
              })}

              {isProcessing && !hasFirstChunk && (
                <div
                  className="bi-agent-loading-card"
                  role="status"
                  aria-live="polite"
                >
                  <ul className="bi-agent-loading-steps">
                    {LOADING_STEPS.map((step, index) => {
                      const state =
                        index < loadingStepIndex
                          ? "done"
                          : index === loadingStepIndex
                            ? "current"
                            : "pending";
                      return (
                        <li
                          key={step.title}
                          className={`bi-agent-loading-step bi-agent-loading-step-${state}`}
                        >
                          <span className="bi-agent-loading-step-icon">
                            {state === "done"
                              ? "\u2713"
                              : state === "current"
                                ? "\u25D4"
                                : "\u25CB"}
                          </span>
                          <span className="bi-agent-loading-step-copy">
                            <strong>{step.title}</strong>
                            <small>{step.subtitle}</small>
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="bi-agent-loading-progress-shell">
                    <div
                      className="bi-agent-loading-progress-bar"
                      style={{ width: `${loadingProgress}%` }}
                    />
                  </div>
                  <p className="bi-agent-loading-progress-label">
                    {loadingProgress}%
                  </p>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          ) : (
            <div className="bi-agent-main-inner">
              <section className="bi-agent-starter-shell">
                <span className="bi-agent-starter-bot">
                  <SmartToyOutlined sx={{ fontSize: 44 }} />
                </span>
                <h2 className="bi-agent-title">What can I help you with?</h2>
                <p className="bi-agent-subtitle">
                  Ask about your clients, or pick a category below
                </p>
                <div className="bi-agent-starter-grid">
                  {STARTER_CARDS.map((card) => (
                    <div
                      key={card.key}
                      className={`bi-agent-starter-card ${card.className}`}
                    >
                      <span className="bi-agent-starter-card-title">
                        {card.icon}
                        {card.title}
                      </span>
                      {card.lines.map((line, idx) => (
                        <button
                          key={`${card.key}-${idx}`}
                          type="button"
                          className="bi-agent-starter-prompt"
                          onClick={() => handleSubmit(line)}
                          disabled={isProcessing}
                        >
                          {line}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}

          {error && (
            <div className="bi-agent-error" role="alert">
              {error}
              <button
                type="button"
                className="bi-agent-error-dismiss"
                onClick={() => setError(null)}
              >
                Dismiss
              </button>
            </div>
          )}

          <div className="bi-agent-input-section">
            <div className="bi-agent-input-row">
              <button
                type="button"
                className={`bi-agent-input-icon ${isListening ? "bi-agent-input-icon-listening" : ""}`}
                title={
                  isListening
                    ? "Stop voice input (your text stays in the box — use Send to submit)"
                    : "Start voice input"
                }
                aria-label={
                  isListening
                    ? "Stop voice input"
                    : "Start voice input"
                }
                onClick={toggleListening}
                disabled={!speechSupport.recognition || isProcessing}
              >
                {isListening ? (
                  <MicNone sx={{ fontSize: 18 }} />
                ) : (
                  <Mic sx={{ fontSize: 18 }} />
                )}
              </button>

              <textarea
                ref={inputRef}
                className="bi-agent-input"
                placeholder="Type or use the mic, then press Send to ask..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                rows={1}
                aria-label="Ask about your clients"
                disabled={isProcessing && !hasFirstChunk}
              />

              <button
                type="button"
                className="bi-agent-send-btn"
                aria-label="Send"
                onClick={() => handleSubmit()}
                disabled={!inputValue.trim() || isProcessing}
              >
                <Send sx={{ fontSize: 18 }} />
              </button>
            </div>

            <p className="bi-agent-footer-text">
              ClientBloom BI Agent - Powered by AI
              {isSpeaking && " - Speaking..."}
            </p>
          </div>
        </main>
      </div>

    </div>
  );
};

export default BiAgent;
